import { PEOPLE_ERROR_CODE_FIX } from 'config/peopleErrorCodes';
import { credentialRefusalMessage } from 'library/helpers/credentials';

// Reading a /people/bulk or /people/access entry result. A 2xx is a report, not a success: each
// entry carries errors[], warnings[] and sometimes errorCodes[], so judge the entry, never the status.

// Nothing was created or found — the entry is a retry.
export const entryFailed = (result) => !result || result.status === 'failed' || !result.personID;

// The person exists but not everything asked for landed. `partial` and a non-empty errors[] both count.
export const entryIncomplete = (result) =>
	!result || result.status === 'failed' || result.status === 'partial' || (result.errors?.length ?? 0) > 0;

const NOTIFICATION_TEXT_MAX = 90;

// Clip server text for a toast subtitle: whole words, an ellipsis past `max` characters.
export const clipNotificationText = (text, max = NOTIFICATION_TEXT_MAX) => {
	const value = String(text ?? '').trim();
	if (value.length <= max) return value;
	const cut = value.slice(0, max);
	return `${cut.slice(0, cut.lastIndexOf(' ') > 0 ? cut.lastIndexOf(' ') : max).replace(/[\s,;:.—-]+$/, '')}…`;
};

// A warning's toast form: the clause before ': ' (what follows is technical detail), clipped.
export const summarizeWarning = (text) => {
	const head = String(text ?? '')
		.split(': ')[0]
		.trim();
	return head ? clipNotificationText(/[.!?…]$/.test(head) ? head : `${head}.`) : '';
};

// errors[] shows clipped plus each code's fix; a credential refusal's one-liner replaces both.
export const entryErrorText = (result) => {
	const short = credentialRefusalMessage(result?.errorCodes);
	if (short) return short;
	const fixes = (result?.errorCodes || []).map((code) => PEOPLE_ERROR_CODE_FIX[code]).filter(Boolean);
	return [...(result?.errors || []).map((e) => clipNotificationText(e)), ...fixes].join(' ') || null;
};

// warnings[] is the only place a reassignment or skipped revoke surfaces: briefed, deduped, capped.
export const collectWarnings = (results = [], max = 4) => {
	const seen = [...new Set(results.flatMap((r) => (r?.warnings || []).map(summarizeWarning)).filter(Boolean))];
	return seen.length > max ? [...seen.slice(0, max), `+${seen.length - max} more.`] : seen;
};

// Toast for one /people/bulk create entry. Warnings (e.g. a visitor's end date not set) are never a success.
export const entryAddedNotice = (result, singular) => {
	const warnings = collectWarnings([result]);
	if (entryIncomplete(result)) {
		return {
			type: 'warning',
			title: 'Added with problems',
			text: [
				credentialRefusalMessage(result?.errorCodes) ||
					`${singular} created, but some access could not be applied.`,
				...warnings
			].join(' ')
		};
	}
	if (warnings.length) {
		return { type: 'warning', title: `${singular} added with warnings`, text: warnings.join(' ') };
	}
	return { type: 'success', title: `${singular} added`, text: `${singular} created successfully.` };
};
